import { useRef, useState, type DragEvent } from 'react';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { JurisdictionCombobox } from '../../tax-settings/JurisdictionCombobox';
import { JurisdictionFact } from '../../tax-settings/JurisdictionFact';
import { INFORMATIONAL_NOTICE } from '../../tax-settings/checklistContent';
import { TaxSetupChecklist } from '../../tax-settings/TaxSetupChecklist';
import { TaxSetupStatusPanel } from '../../tax-settings/TaxSetupStatusPanel';
import { jurisdictionLabel, useJurisdictionCatalog } from '../../tax-settings/useJurisdictionCatalog';
import { EoInput, EoSelect } from '../components/Fields';
import { SectionFrame } from '../components/SectionFrame';
import { formatDisplayDate } from '../format';
import { employeeOnboardingGateway } from '../index';
import type { JurisdictionValueDto, ReciprocityDto } from '../types';
import type { TaxSectionProps } from './taxProps';
import { buildStateInput, certificateError, moneyError, moneyText, parseMoney } from './taxValidation';

type AddErrors = { state?: string; value?: string; type?: string };
const dropError = (errors: AddErrors, key: keyof AddErrors): AddErrors => {
  const next = { ...errors };
  delete next[key];
  return next;
};

const SYSTEM_DERIVED = 'Set by the system from your addresses and your employer. You cannot edit this.';
const LOCAL_SOURCE = 'Not determined yet. EPN cannot yet work out local taxes from your addresses, and it never assumes there are none. A reviewer will check.';
const OVERRIDE_NOTE = 'You cannot change these here. If one looks wrong, correct your home address in Employee Setup or ask your payroll administrator about your work location.';

const RECIPROCITY_CHIP: Record<ReciprocityDto['status'], string> = {
  NotApplicable: 'Not applicable',
  Unknown: 'Unknown. Pending review',
  EligibleCertificateNeeded: 'Eligible. Certificate needed',
  SubmittedPendingReview: 'Submitted. Pending review',
  Approved: 'Approved',
  Rejected: 'Rejected',
  NeedsReview: 'Needs review',
  Expired: 'Expired. Renewal needed',
  Revoked: 'Revoked',
};

/** A state fact: the name from the server with its availability badge, and a source line. Not a value unless the server verified it. */
function StateFact({ label, item, testId, verifiedSource }: { label: string; item: JurisdictionValueDto; testId: string; verifiedSource: string }) {
  const catalog = useJurisdictionCatalog();
  const verified = item.status === 'Verified' && item.value ? item.value : null;
  const statusText: Record<JurisdictionValueDto['status'], string> = { Verified: '', Unknown: 'Unknown', PendingReview: 'Pending review', NotApplicable: 'N/A' };
  return (
    <JurisdictionFact
      label={label}
      text={verified}
      emptyText={statusText[item.status]}
      entry={catalog.findByName(verified)}
      source={verified ? verifiedSource : item.note}
      help={item.note ? `${SYSTEM_DERIVED} ${item.note}` : SYSTEM_DERIVED}
      testId={testId}
    />
  );
}

function LocalFact({ label, testId }: { label: string; testId: string }) {
  return <JurisdictionFact label={label} text={null} emptyText="Not determined yet" chip="NeedsReview" source={LOCAL_SOURCE} help={`${SYSTEM_DERIVED} ${LOCAL_SOURCE}`} testId={testId} />;
}

/** Tax Settings (Figma 05, 06, simplified). The jurisdictions are read-only facts with their sources, the guided checklist says what each tax area needs and who acts,
 * and the reciprocity request is a guided workflow. A certificate is a REQUEST, never an exemption, and nothing here changes withholding. */
export function TaxSettingsSection({ taxes, refresh, next, back }: TaxSectionProps) {
  const { settings, state } = taxes;
  const recip = settings.reciprocity;
  const extra = settings.additionalWithholding;
  const catalog = useJurisdictionCatalog();
  // A territory has no withholding election in EPN, so it is never offered for an additional withholding request.
  const addOptions = state.options.filter((o) => o.ruleKind !== 'NotYetSupported');

  const [addState, setAddState] = useState(extra.state ?? '');
  const [addValue, setAddValue] = useState(moneyText(extra.value));
  const [addType, setAddType] = useState(extra.type ?? '');
  const [addErrors, setAddErrors] = useState<AddErrors>({});
  const [requesting, setRequesting] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  const renewing = recip.status === 'Expired' || recip.status === 'Revoked' || recip.status === 'Rejected';
  const requestButton = renewing ? 'Send a new certificate' : 'Request a reciprocity exemption';

  function choose(f: File | undefined) {
    if (!f) return;
    const problem = certificateError(f);
    setFileError(problem ?? null);
    setFile(problem ? null : f);
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    choose(e.dataTransfer.files[0]);
  }

  async function sendCertificate(): Promise<boolean> {
    if (!file) return true;
    try {
      await employeeOnboardingGateway.submitReciprocityCertificate(file);
      setFile(null);
      setRequesting(false);
      await refresh();
      return true;
    } catch (err) {
      setError(friendlyErrorMessage(err, 'We could not send your certificate. Please try again.'));
      return false;
    }
  }

  async function submitCertificateOnly() {
    setError(null);
    setBusy(true);
    try {
      await sendCertificate();
    } finally {
      setBusy(false);
    }
  }

  async function withdraw() {
    setError(null);
    setBusy(true);
    try {
      await employeeOnboardingGateway.withdrawReciprocityRequest();
      await refresh();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'We could not withdraw your request. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setError(null);
    const anyAdd = addState !== '' || addValue.trim() !== '' || addType !== '';
    const e: AddErrors = {};
    if (anyAdd) {
      if (!addState) e.state = 'State is required.';
      if (!addType) e.type = 'Type is required.';
      const v = moneyError(addValue, 'Value', true);
      if (v) e.value = v;
    }
    setAddErrors(e);
    if (Object.keys(e).length > 0) return;
    if (anyAdd && !state.draft) {
      setError('Complete State Income Tax first, then request additional withholding here.');
      return;
    }

    setBusy(true);
    try {
      if (!(await sendCertificate())) return;
      const hadExtra = extra.state !== null || extra.value !== null || extra.type !== null;
      if (state.draft && (anyAdd || hadExtra)) {
        // The server replaces the whole state draft, so this screen sends the saved state answers back with the request.
        await employeeOnboardingGateway.saveStateTax(
          buildStateInput(state.draft, anyAdd ? { state: addState, value: parseMoney(addValue), type: 'Amount' } : { state: null, value: null, type: null }),
        );
      }
      await refresh();
      next();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'We could not save your tax settings. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  function clearDraft() {
    setAddState('');
    setAddValue('');
    setAddType('');
    setAddErrors({});
    setRequesting(false);
    setFile(null);
    setFileError(null);
    setError(null);
  }

  return (
    <SectionFrame
      title="Tax Settings"
      onSubmit={submit}
      onBack={back}
      onClearDraft={clearDraft}
      clearDraftDescription="This clears the additional withholding fields and any certificate you picked on this screen. Anything you already saved or sent stays until you save again."
      busy={busy}
      error={error}
    >
      <p className="eo-note" data-testid="tax-informational">
        {INFORMATIONAL_NOTICE}
      </p>

      {taxes.taxSetup ? (
        <TaxSetupStatusPanel status={taxes.taxSetup} />
      ) : (
        <p className="eo-note" data-testid="tax-setup-unavailable">
          The tax setup status is not available right now. Nothing is shown as complete.
        </p>
      )}

      <h3 className="eo-subtitle">Where you work and live</h3>
      <div className="eo-facts ts-facts">
        <StateFact label="Tax Work State" item={settings.workState} testId="fact-work-state" verifiedSource="Determined from your primary work location." />
        <LocalFact label="Local Tax Jurisdiction – Work" testId="fact-work-local" />
        <StateFact label="Tax Residence State" item={settings.residenceState} testId="fact-residence-state" verifiedSource="From your home address." />
        <LocalFact label="Local Tax Jurisdiction – Residence" testId="fact-residence-local" />
      </div>
      <p className="eo-note" data-testid="override-note">
        {OVERRIDE_NOTE}
      </p>

      {taxes.taxSetup && (
        <>
          <h3 className="eo-subtitle">Your tax setup checklist</h3>
          <TaxSetupChecklist status={taxes.taxSetup} view="employee" />
        </>
      )}

      <div className="eo-tax-panels">
        <section className="eo-tax-card" aria-labelledby="eo-addl-title">
          <h3 id="eo-addl-title" className="eo-tax-card__title">
            Additional State Tax Withholding
          </h3>
          <JurisdictionCombobox
            label="State"
            items={catalog.items}
            allowedCodes={addOptions.map((o) => o.code)}
            value={addState}
            error={addErrors.state}
            disabled={addOptions.length === 0 || catalog.loading}
            loading={catalog.loading}
            clearable
            onChange={(code) => {
              setAddState(code);
              setAddErrors((p) => dropError(p, 'state'));
            }}
          />
          <div className="eo-tax-card__pair">
            <EoInput
              label="Value"
              inputMode="decimal"
              autoComplete="off"
              placeholder="00.00"
              value={addValue}
              error={addErrors.value}
              disabled={addOptions.length === 0}
              onChange={(e) => {
                setAddValue(e.target.value);
                setAddErrors((p) => dropError(p, 'value'));
              }}
            />
            <EoSelect label="Type" value={addType} error={addErrors.type} disabled={addOptions.length === 0} onChange={(e) => {
                setAddType(e.target.value);
                setAddErrors((p) => dropError(p, 'type'));
              }}
            >
              <option value="">Select</option>
              <option value="Amount">Amount ($)</option>
              <option value="Percent" disabled>
                Percent (not available yet)
              </option>
            </EoSelect>
          </div>
          <p className="eo-field__hint">A voluntary extra amount per pay period. It can only add to required withholding. It is a draft: payroll does not use it yet.</p>
        </section>

        <section className="eo-tax-card" aria-labelledby="eo-recip-title">
          <h3 id="eo-recip-title" className="eo-tax-card__title">
            Reciprocity Exemption
          </h3>
          <p className={`eo-chip eo-chip--${recip.status}`} data-testid="reciprocity-status">
            {recip.status === 'Approved' && recip.effectiveFrom ? `Approved. Effective ${formatDisplayDate(recip.effectiveFrom)}` : RECIPROCITY_CHIP[recip.status] ?? 'Needs review'}
          </p>
          <p id="eo-recip-note" className="eo-note" data-testid="reciprocity-reason">
            {recip.reason}
          </p>
          {(recip.status === 'Approved' || recip.status === 'Expired') && (
            <p className="eo-note" data-testid="reciprocity-expiry">
              {recip.expiresOn ? `${recip.status === 'Expired' ? 'Expired' : 'Expires'} ${formatDisplayDate(recip.expiresOn)}.` : 'No expiry date is recorded.'}
            </p>
          )}
          {recip.reviewNote && recip.status === 'Rejected' && <p className="eo-note">Reviewer note: {recip.reviewNote}</p>}

          {recip.requiredForm && (
            <ol className="eo-recip-steps" data-testid="reciprocity-steps">
              <li>
                <strong>Eligibility.</strong> You live in {jurisdictionLabel(recip.residenceState)} and work in {jurisdictionLabel(recip.workState)}, and those two states have a
                reciprocity agreement.
              </li>
              <li>
                <strong>Required form.</strong> {recip.requiredForm}: {recip.requiredFormDescription}
              </li>
              <li>
                <strong>Upload and review.</strong> You send the signed form. Your payroll administrator reviews it and may set an expiry date. When it expires, you send a new
                one.
              </li>
            </ol>
          )}

          {recip.canSubmit && !requesting && (
            <button type="button" className="eo-btn eo-btn--outline" onClick={() => setRequesting(true)}>
              {requestButton}
            </button>
          )}

          {recip.canSubmit && requesting && recip.requiredForm && (
            <div className="eo-recip-upload" data-testid="reciprocity-upload">
              <p className="eo-note">
                <strong>{recip.requiredForm}</strong>: {recip.requiredFormDescription}
              </p>
              <div
                className={`eo-dropzone${dragging ? ' eo-dropzone--over' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
              >
                <input
                  ref={picker}
                  id="eo-recip-file"
                  type="file"
                  className="eo-sr-only"
                  accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                  onChange={(e) => choose(e.target.files?.[0])}
                />
                <label htmlFor="eo-recip-file" className="eo-dropzone__label">
                  <span className="eo-dropzone__icon" aria-hidden="true" />
                  <span>
                    <u>Click to upload</u> or drag and drop
                  </span>
                  <span className="eo-dropzone__hint">PDF, PNG or JPG, up to 10 MB</span>
                </label>
              </div>
              {fileError && (
                <p className="eo-field__error" role="alert">
                  {fileError}
                </p>
              )}
              {file && (
                <p className="eo-note" data-testid="reciprocity-file">
                  Ready to send: {file.name}{' '}
                  <button type="button" className="eo-link-button" onClick={() => setFile(null)}>
                    Remove
                  </button>
                </p>
              )}
              <button type="button" className="eo-btn eo-btn--outline" disabled={!file || busy} onClick={() => void submitCertificateOnly()}>
                Send certificate for review
              </button>
              <p className="eo-field__hint">
                Sending a certificate is a request. Your withholding does not change until your payroll administrator approves it. Even then, EPN does not apply it to
                payroll yet.
              </p>
            </div>
          )}

          {recip.canWithdraw && (
            <button type="button" className="eo-link-button eo-link-button--left" disabled={busy} onClick={() => void withdraw()}>
              Withdraw this request
            </button>
          )}
        </section>
      </div>
    </SectionFrame>
  );
}
